/**
 * Permisos del tenant en el front (cortesía). El candado real es el backend.
 * S196: menú lateral. S200: pestañas internas de cada hub.
 */
import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '@/lib/api';

const NINGUNO = 'ninguno';
const LECTURA = 'lectura';
const ESCRITURA = 'escritura';
const FUERZA = { [NINGUNO]: 0, [LECTURA]: 1, [ESCRITURA]: 2 };

/** Prefijos más largos primero (moduleForPath). */
export const NAV_MODULE_BY_PREFIX = [
  // Business granulares
  { prefix: '/tenant/business/flows', module: 'flows' },
  { prefix: '/tenant/business/team', module: 'users' },
  { prefix: '/tenant/business/assignment', module: 'assignment' },
  { prefix: '/tenant/business/permissions', module: 'users' },
  { prefix: '/tenant/business/connections', module: 'integrations' },
  { prefix: '/tenant/business/locations', module: 'locations' },
  { prefix: '/tenant/business/hotel', module: 'hotel' },
  { prefix: '/tenant/business/taller', module: 'taller' },
  { prefix: '/tenant/business/clinica', module: 'expediente' },
  { prefix: '/tenant/business/restaurante', module: 'restaurante' },
  { prefix: '/tenant/business/spa', module: 'spa' },
  { prefix: '/tenant/business/catalog', module: 'inventory' },
  { prefix: '/tenant/business/developments', module: 'real_estate_erp' },
  { prefix: '/tenant/business/profile', module: 'settings' },
  { prefix: '/tenant/business/resumen', module: 'settings' },
  { prefix: '/tenant/business', module: 'settings' },
  // AI granulares
  { prefix: '/tenant/ai/knowledge', module: 'knowledge' },
  { prefix: '/tenant/ai/training', module: 'ai_training' },
  { prefix: '/tenant/ai/skills', module: 'ai_skills' },
  { prefix: '/tenant/ai/tools/mcp', module: 'mcp' },
  { prefix: '/tenant/ai/tools', module: 'ai_tools' },
  { prefix: '/tenant/ai/orchestrator', module: 'orchestrator' },
  { prefix: '/tenant/ai/triggers', module: 'flows' },
  { prefix: '/tenant/ai/voice-calls', module: 'integrations' },
  { prefix: '/tenant/ai', module: 'ai' },
  // Marketing granulares
  { prefix: '/tenant/marketing/origenes', module: 'lead_sources' },
  { prefix: '/tenant/marketing/qr-campaigns', module: 'lead_sources' },
  { prefix: '/tenant/marketing/segments', module: 'audiences' },
  { prefix: '/tenant/marketing/audiences', module: 'audiences' },
  { prefix: '/tenant/marketing/contacts', module: 'contacts' },
  { prefix: '/tenant/marketing/schedules', module: 'campaign_schedules' },
  { prefix: '/tenant/marketing/delivery', module: 'campaign_delivery' },
  { prefix: '/tenant/marketing/automations', module: 'marketing' },
  { prefix: '/tenant/marketing/campaigns', module: 'marketing' },
  { prefix: '/tenant/marketing/create', module: 'marketing' },
  { prefix: '/tenant/marketing', module: 'marketing' },
  // Agenda
  { prefix: '/tenant/agenda/resources', module: 'agenda_config' },
  { prefix: '/tenant/agenda/floor-plan', module: 'restaurante' },
  { prefix: '/tenant/agenda', module: 'agenda' },
  // CRM
  { prefix: '/tenant/crm/pipeline', module: 'pipeline' },
  { prefix: '/tenant/crm', module: 'crm' },
  // Resto hubs
  { prefix: '/tenant/administracion/mesa-de-servicio', module: 'real_estate_warranty' },
  { prefix: '/tenant/administracion/condominal', module: 'real_estate_condo' },
  { prefix: '/tenant/administracion', module: 'pos' },
  { prefix: '/tenant/inbox', module: 'inbox' },
  { prefix: '/tenant/email', module: 'email' },
  { prefix: '/tenant/whatsapp', module: 'marketing' },
  { prefix: '/tenant/plan', module: 'billing' },
  { prefix: '/tenant/integrations', module: 'integrations' },
  { prefix: '/tenant/setup', module: 'flows' },
  { prefix: '/tenant/reports', module: 'reports' },
  { prefix: '/tenant/social-ads/publicaciones', module: 'social' },
  { prefix: '/tenant/social-ads/meta-ads', module: 'meta_ads' },
  { prefix: '/tenant/social-ads', module: 'marketing' },
  { prefix: '/tenant/web', module: 'web' },
  { prefix: '/tenant', module: 'dashboard', end: true },
];

export function moduleForPath(pathname) {
  const p = pathname || '';
  for (const row of NAV_MODULE_BY_PREFIX) {
    if (row.end) {
      if (p === '/tenant' || p === '/tenant/') return row.module;
      continue;
    }
    if (p === row.prefix || p.startsWith(row.prefix + '/')) return row.module;
  }
  return null;
}

function pathOfTab(tab) {
  return tab.path || tab.to || '';
}

/** ¿La ruta actual corresponde a esta pestaña? */
export function tabMatchesPath(tab, pathname) {
  const base = pathOfTab(tab);
  if (!base) return false;
  if (tab.end) {
    return pathname === base || pathname === base + '/';
  }
  return pathname === base || pathname.startsWith(base + '/');
}

let enVuelo = null;

function pedirMapa() {
  if (!enVuelo) {
    enVuelo = api.get('/tenant/mis-permisos')
      .then((r) => r.data?.modulos || {})
      .catch(() => null);
  }
  return enVuelo;
}

export function olvidarPermisos() {
  enVuelo = null;
}

export function usePermisos() {
  const [modulos, setModulos] = useState(null);

  useEffect(() => {
    let vivo = true;
    pedirMapa().then((m) => { if (vivo) setModulos(m); });
    return () => { vivo = false; };
  }, []);

  const nivel = (clave) => (modulos ? (modulos[clave] || NINGUNO) : null);

  const alcanza = (clave, minimo) => {
    const n = nivel(clave);
    if (n === null) return true;
    return FUERZA[n] >= FUERZA[minimo];
  };

  return {
    cargado: modulos !== null,
    modulos,
    nivel,
    puedeVer: (clave) => !clave || alcanza(clave, LECTURA),
    puedeEditar: (clave) => !clave || alcanza(clave, ESCRITURA),
    puedeVerRuta: (pathname) => {
      const m = moduleForPath(pathname);
      if (!m) return true;
      return alcanza(m, LECTURA);
    },
  };
}

/**
 * Filtra pestañas de un hub por módulo y redirige si la URL actual no es visible.
 * @param {Array<{path?:string,to?:string,module?:string,end?:boolean}>} tabs
 */
export function useHubTabs(tabs) {
  const { puedeVer, cargado } = usePermisos();
  const location = useLocation();
  const navigate = useNavigate();

  const visible = useMemo(() => {
    if (!Array.isArray(tabs)) return [];
    // Mientras carga, no ocultamos (evita flash vacío); al cargar se filtra.
    if (!cargado) return tabs;
    return tabs.filter((t) => !t.module || puedeVer(t.module));
  }, [tabs, puedeVer, cargado]);

  useEffect(() => {
    if (!cargado || !visible.length) return;
    const onForbidden = tabs.some(
      (t) => tabMatchesPath(t, location.pathname) && t.module && !puedeVer(t.module),
    );
    if (onForbidden) {
      const first = pathOfTab(visible[0]);
      if (first) navigate(first, { replace: true });
    }
  }, [cargado, visible, tabs, location.pathname, navigate, puedeVer]);

  return { tabs: visible, cargado, sinAcceso: cargado && visible.length === 0 };
}
